import { displayOf, resolveFields, type StoryCtx } from './ctx.js';
import { materialOf, materialText } from './material.js';
import { keysOf, mentionsIn, plainRefs } from './refs.js';

export interface SearchHit {
  /** Node, zu dem die Oberfläche navigiert (null: Eintrag ohne Instanz) */
  nodeId: string | null;
  entityId: string | null;
  kind: 'title' | 'ref' | 'text' | 'field' | 'material';
  /** Anzeigename des Treffers */
  label: string;
  /** Textausschnitt um die Fundstelle */
  snippet: string;
  docId?: string;
  /** Name des Textdokuments (Treffer im Text) */
  docName?: string;
}

const snippetAround = (text: string, index: number, length: number): string => {
  const from = Math.max(0, index - 30);
  const to = Math.min(text.length, index + length + 40);
  return `${from > 0 ? '…' : ''}${text.slice(from, to).replace(/\s+/g, ' ')}${to < text.length ? '…' : ''}`;
};

const ORDER: Record<SearchHit['kind'], number> = { ref: 0, title: 1, text: 2, field: 3, material: 4 };

/**
 * Sucht in Titeln, Feldern, Textblöcken und Verweisschlüsseln. Eine Suche mit „@“ findet Einträge mit diesem Schlüssel und
 * alle Textstellen, die ihn verwenden. Das Ergebnis ist nach Art und Name geordnet.
 */
export function searchStory(ctx: StoryCtx, query: string, limit = 40): SearchHit[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [];
  const hits: SearchHit[] = [];
  const byRef = q.startsWith('@');
  const key = q.replace(/^@/, '');

  for (const entity of ctx.story.entities) {
    // Hauptschlüssel und weitere Bezeichnungen; angezeigt wird der passende
    const matched = keysOf(entity).find((k) => (byRef ? k.startsWith(key) : k.includes(q)));
    if (!matched) continue;
    const instances = ctx.story.nodes.filter((n) => n.entityId === entity.id);
    const type = ctx.types.get(entity.typeId);
    const label = String(entity.fields[type?.titleKey ?? ''] ?? '').trim() || type?.name || entity.refKey;
    if (instances.length === 0) hits.push({ nodeId: null, entityId: entity.id, kind: 'ref', label, snippet: `@${matched}` });
    for (const n of instances) hits.push({ nodeId: n.id, entityId: entity.id, kind: 'ref', label, snippet: `@${matched}` });
  }

  for (const node of ctx.story.nodes) {
    if (node.kind === 'story') continue;
    const d = displayOf(ctx, node);
    if (!byRef && d.title.toLowerCase().includes(q)) hits.push({ nodeId: node.id, entityId: node.entityId, kind: 'title', label: d.title, snippet: d.typeName });
    for (const b of node.documents) {
      if (byRef) {
        const m = mentionsIn(b.text).find((x) => x.key.startsWith(key));
        if (m) hits.push({ nodeId: node.id, entityId: null, kind: 'text', label: d.title, snippet: snippetAround(b.text, m.index, m.length), docId: b.id, docName: b.name });
        continue;
      }
      const at = b.text.toLowerCase().indexOf(q);
      if (at >= 0) hits.push({ nodeId: node.id, entityId: null, kind: 'text', label: d.title, snippet: plainRefs(snippetAround(b.text, at, q.length)), docId: b.id, docName: b.name });
    }
    if (byRef) continue;
    // das Material einer Szene: ein Treffer je Szene
    for (const m of materialOf(node)) {
      const t = materialText(m);
      const at = t.toLowerCase().indexOf(q);
      if (at < 0) continue;
      hits.push({ nodeId: node.id, entityId: null, kind: 'material', label: d.title, snippet: snippetAround(t, at, q.length) });
      break;
    }
    const values: string[] = [...Object.values(resolveFields(ctx, node)), ...node.dynamicFields.map((f) => f.value)].filter((v): v is string => typeof v === 'string');
    for (const v of values) {
      const at = v.toLowerCase().indexOf(q);
      if (at >= 0 && !v.startsWith('data:') && v.toLowerCase() !== d.title.toLowerCase()) {
        hits.push({ nodeId: node.id, entityId: node.entityId, kind: 'field', label: d.title, snippet: plainRefs(snippetAround(v, at, q.length)) });
        break;
      }
    }
  }
  const seen = new Set<string>();
  return hits
    .filter((h) => {
      const k = `${h.nodeId}|${h.entityId}|${h.kind}|${h.docId ?? ''}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => ORDER[a.kind] - ORDER[b.kind] || a.label.localeCompare(b.label, 'de'))
    .slice(0, limit);
}
